import { BANKS } from "@/utils/enums";

/**
 * Pulls a transaction id out of OCR'd receipt text.
 *
 * This is a faithful port of the Flutter app's `TransactionIdExtractor`
 * (lib/src/features/verification/application/transaction_id_extractor.dart) so
 * the web scanner extracts exactly what the mobile app does.
 *
 * Strategy, in order of confidence:
 *  1. Bank-specific shape (CBE and Bank of Abyssinia references start with `FT`).
 *  2. A value following a label like "transaction number" / "receipt no".
 *  3. The strongest free-standing alphanumeric token (letters + digits),
 *     which excludes phone numbers and amounts.
 */

// CBE and BOA references start with `FT`, sometimes split from the rest by a
// space or newline in OCR. The capture stops at the next non-alphanumeric. We
// avoid a lookbehind (unsupported on older mobile Safari) and instead verify the
// character before each match isn't alphanumeric, which keeps us from matching
// the "FT" inside words like "SHIFT".
const FT_PREFIXED = /FT\s*([A-Z0-9]{6,})/g;
const LABELED =
    /(?:TRANSACTION|RECEIPT|REF(?:ERENCE)?|TXN)\s*(?:NUMBER|NUM|NO|ID|REF)?\s*[:#-]?\s*([A-Z0-9]{6,})/;
const TOKEN = /[A-Z0-9]{6,}/g;
const LETTER = /[A-Z]/;
const DIGIT = /[0-9]/;

function looksLikeTxnId(token: string): boolean {
    if (token.length < 8 || token.length > 20) return false;
    return LETTER.test(token) && DIGIT.test(token);
}

/**
 * Extracts a transaction id from `ocrText` for the given `bank`, or `null` when
 * none can be found confidently. `bank` is a value from {@link BANKS}.
 */
export function extractTransactionId(bank: string, ocrText: string): string | null {
    if (!ocrText || !ocrText.trim()) return null;
    const upper = ocrText.toUpperCase();

    if (bank === BANKS.cbe || bank === BANKS.boa) {
        const ft = firstFtReference(upper);
        if (ft) return ft;
    }

    LABELED.lastIndex = 0;
    const labeled = LABELED.exec(upper)?.[1] ?? null;
    if (labeled && looksLikeTxnId(labeled)) return labeled;

    const candidates: string[] = [];
    TOKEN.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = TOKEN.exec(upper)) !== null) {
        if (looksLikeTxnId(match[0])) candidates.push(match[0]);
    }
    if (candidates.length === 0) return null;

    // Prefer the longest candidate; it's almost always the reference number.
    candidates.sort((a, b) => b.length - a.length);
    return candidates[0];
}

/** Leftmost `FT…` reference not glued onto the end of another word. */
function firstFtReference(upper: string): string | null {
    FT_PREFIXED.lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = FT_PREFIXED.exec(upper)) !== null) {
        const before = match.index > 0 ? upper[match.index - 1] : "";
        if (!/[A-Z0-9]/.test(before)) {
            return `FT${match[1]}`;
        }
    }
    return null;
}
